import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import titleize from "titleize";
import {
    Alert,
    Button,
    Empty,
    MenuProps,
    Modal,
    Result,
    Space,
    Spin,
    Typography,
    Row,
} from "../../design-system";
import {
    ProjectAnalysisStatus,
    getLatestAnalysis,
    canDo,
    ProjectAction,
    getAnalysisByVersion,
    ProjectStatus,
    ProjectType,
    AnalysisOutputType,
    AnalysisType,
    ClosingOutcome,
    CLOSING_OUTCOMES_BY_PROJECT_TYPE,
} from "../../types/ProjectTypes";
import { isSuperAdmin } from "../../types/UserGroup";
import { getProject, closeProject } from "../../common/api/ProjectClient";
import { useUserState } from "../../common/UserContext";
import InfinitySpinner from "../../common/components/InfinitySpinner";
import { ModalSaveAnalysisOutput } from "./components/ModalSaveAnalysisOutput";
import { ModalRequestChange } from "./components/ModalRequestChange";
import { ProjectCard } from "./components/ProjectCard";
import { InputEditor } from "./components/InputEditor";
import { InputViewer } from "./components/InputViewer";
import { DealOutput } from "./components/DealOutput";
import { RfxOutput } from "./components/RfxOutput";
import { FlexibleOutput } from "./components/FlexibleOutput";
import { CompletedRfxView } from "./components/CompletedRfxView";
import { SourceBanner } from "./components/SourceBanner";
import styles from "./ProjectView.module.less";
import { ProjectViewState } from "./ProjectViewState";

export const ProjectView: FC = () => {
    const navigate = useNavigate();
    const { me } = useUserState();
    const { projectId, version } = useParams<{ projectId: string; version?: string }>();
    const [state, setState] = useState<ProjectViewState | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [requestChangeModalOpen, setRequestChangeModalOpen] = useState(false);
    const [saveOutputModalOpen, setSaveOutputModalOpen] = useState(false);
    const [closeProjectModalOpen, setCloseProjectModalOpen] = useState(false);
    const [isClosingProject, setIsClosingProject] = useState<ClosingOutcome | undefined>(undefined);
    const [inputModalOpen, setInputModalOpen] = useState(false);

    const isUserSuperAdmin = isSuperAdmin(me.roles || []);

    // Fetch the project from the URL param and update the local state.
    // Keeps the previous state mounted while refetching so in-place updates
    // (e.g. toggling a vendor selection) don't unmount the screen.
    const fetchProject = async () => {
        try {
            const project = await getProject(Number(projectId));
            const analysis = (version && getAnalysisByVersion(project, Number(version))) || getLatestAnalysis(project);
            setState({ project, analysis });
            if (analysis.status === ProjectAnalysisStatus.REQUIRE_USER_ACTION
                || (analysis.status === ProjectAnalysisStatus.DRAFT && analysis.version > 1)) {
                setInputModalOpen(project.status !== ProjectStatus.COMPLETED);
            }
        } catch (error) {
            setState(null); // we will show "Project Not Found"
        } finally {
            setIsLoading(false);
        }
    };

    // Refetch the project when projectId changes. Reset state + spinner so the
    // previous project's content doesn't briefly flash while the new one loads.
    useEffect(() => {
        setIsLoading(true);
        setState(null);
        fetchProject();
    }, [projectId]);

    // Re-derive state when version changes (no fetch needed)
    useEffect(() => {
        if (state) {
            const analysis = version
                ? getAnalysisByVersion(state.project, Number(version)) || getLatestAnalysis(state.project)
                : getLatestAnalysis(state.project);
            setState({ project: state.project, analysis });
        }
    }, [version]);

    // Sync URL to match state
    useEffect(() => {
        if (state) {
            let path = '';
            if (state.project.status === ProjectStatus.DRAFT) {
                path = `/project/${projectId}/intake`;
            } else {
                path = `/project/${projectId}/analysis/${state.analysis.version}`;
            }
            navigate(path, { replace: true });
        }
    }, [state]);

    if (isLoading) {
        return (
            <div className={styles.container} style={{ textAlign: "center", paddingTop: 100 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!state) {
        return (
            <div className={styles.container}>
                <Empty description="Project not found" />
            </div>
        );
    }

    const { project, analysis } = state;
    const { status } = analysis;

    // ── Intake (project.status = draft) ─────────────────────────

    const renderIntake = () => {
        return (
            <>
                <Row align="middle" justify="space-between" className={styles.contentHeader}>
                    <span className={styles.contentTitle}>Intake</span>
                </Row>
                <div className={styles.tabContent}>
                    <InputEditor
                        state={state}
                        readOnly={!canDo(project, ProjectAction.EDIT)}
                        onSaveDraft={fetchProject}
                        onSubmit={async () => {
                            await fetchProject();
                            navigate(`/project/${project.id}`);
                        }}
                    />
                </div>
            </>
        );
    };

    // ── Analysis (project.status = active) ──────────────────────────

    // admin actions
    const adminActionsMenuItems: MenuProps["items"] = [];
    if (isUserSuperAdmin) {
        if (status === ProjectAnalysisStatus.REQUIRE_USER_ACTION || status === ProjectAnalysisStatus.SUBMITTED) {
            adminActionsMenuItems.push({
                key: "RequestChanges",
                label: "Request changes",
                onClick: () => setRequestChangeModalOpen(true),
            });
        }
        if (status !== ProjectAnalysisStatus.DRAFT) {
            adminActionsMenuItems.push({
                key: "SaveAnalysisOutput",
                label: "Save/Edit analysis output",
                onClick: () => setSaveOutputModalOpen(true),
            });
        }
        if (project.status === ProjectStatus.ACTIVE) {
            adminActionsMenuItems.push({
                key: "CloseProject",
                label: "Close Project",
                onClick: () => setCloseProjectModalOpen(true),
            });
        }
    }

    const handleCloseProject = async (outcome: ClosingOutcome) => {
        try {
            setIsClosingProject(outcome);
            await closeProject(project.id, outcome);
            await fetchProject();
            setCloseProjectModalOpen(false);
            navigate(`/project/${project.id}`);
        } finally {
            setIsClosingProject(undefined);
        }
    };

    const renderOutputContent = () => {
        // "Complete" analysis (closing without output) — show the input
        if (analysis.type === AnalysisType.COMPLETE) {
            const firstInput = analysis.inputs?.[0];
            const isCompletedRfx = project.type === ProjectType.RFX
                && project.status === ProjectStatus.COMPLETED;

            if (isCompletedRfx) {
                return (
                    <div className={styles.tabContent}>
                        <Space direction="vertical" size="large" fullWidth>
                            <CompletedRfxView project={project} onUpdate={fetchProject} />
                            {firstInput && <InputViewer input={firstInput} />}
                        </Space>
                    </div>
                );
            }
            if (firstInput) {
                return (
                    <div className={styles.tabContent}>
                        <InputViewer input={firstInput} />
                    </div>
                );
            }
            return (
                <div className={styles.tabContent}>
                    <Empty description="No analysis available" />
                </div>
            );
        }

        // show output if analysis is completed or superadmin previews it
        if (status === ProjectAnalysisStatus.COMPLETED || isUserSuperAdmin) {
            switch (analysis.outputType) {
                case AnalysisOutputType.DEAL:
                    return <DealOutput project={project} analysis={analysis} />;
                case AnalysisOutputType.RFX:
                    return <RfxOutput project={project} analysis={analysis} />;
                case AnalysisOutputType.FLEXIBLE:
                    return <FlexibleOutput project={project} analysis={analysis} />;
            }
        }

        // otherwise, shows the analysis as pending.
        if (status === ProjectAnalysisStatus.SUBMITTED) {
            return (
                <div className={styles.tabContent}>
                    <Result
                        icon={<InfinitySpinner size={64} spin={true} />}
                        subTitle={
                            <>
                                We're reviewing your documents and preparing your negotiation playbook.
                                <br />
                                You'll be notified when it's ready.
                            </>
                        }
                    />
                    {canDo(project, ProjectAction.EDIT) && (
                        <div className={styles.pendingUpdateHint}>
                            Need to make changes?{" "}
                            <Button type="link" size="small" onClick={() => setInputModalOpen(true)}>
                                Update your analysis
                            </Button>
                        </div>
                    )}
                </div>
            );
        }
    };

    // ── Render ───────────────────────────────────────────────────

    const isFlexibleOutput = analysis.outputType === AnalysisOutputType.FLEXIBLE;
    const content = (project.status === ProjectStatus.DRAFT) ? renderIntake() : renderOutputContent();

    return (
        <div className={styles.container}>

            {project.source && <SourceBanner source={project.source} />}

            <div className={styles.stickyHeader}>
                <ProjectCard
                    project={project}
                    selectedVersion={analysis.version}
                    onAnalysisSelect={(v) => navigate(`/project/${project.id}/analysis/${v}`)}
                    onAnalysisUpdate={() => setInputModalOpen(true)}
                    onUpdate={fetchProject}
                    showActions
                    adminActionsMenuItems={adminActionsMenuItems}
                    noMarginBottom
                />
            </div>

            <div key={analysis.version}>
                {isFlexibleOutput ? content : <div className={styles.legacyContentContainer}>{content}</div>}
            </div>

            <ModalRequestChange
                open={requestChangeModalOpen}
                state={state}
                onClose={() => setRequestChangeModalOpen(false)}
                onSuccess={fetchProject}
            />

            <ModalSaveAnalysisOutput
                open={saveOutputModalOpen}
                state={state}
                onClose={() => setSaveOutputModalOpen(false)}
                onSuccess={fetchProject}
            />

            <Modal
                open={closeProjectModalOpen}
                title={"Close project"}
                footer={(
                    <Space align="end">
                        <Button size="large" disabled={!!isClosingProject} onClick={() => setCloseProjectModalOpen(false)}>Cancel</Button>
                        {CLOSING_OUTCOMES_BY_PROJECT_TYPE[project.type].map((outcome) => (
                            <Button
                                key={outcome}
                                size="large"
                                danger={true}
                                disabled={!!isClosingProject}
                                loading={isClosingProject === outcome}
                                onClick={() => handleCloseProject(outcome)}
                            >
                                Close Project: {titleize(outcome)}
                            </Button>
                        ))}
                    </Space>
                )}
                width={600}
                destroyOnClose
                onCancel={() => setCloseProjectModalOpen(false)}
            >
                <div>This will close project <strong>{project.name}</strong> <em>without</em> sending notification to followers.</div>
            </Modal>

            <Modal
                open={inputModalOpen}
                title={status === ProjectAnalysisStatus.SUBMITTED
                    || status === ProjectAnalysisStatus.REQUIRE_USER_ACTION
                        ? "Update analysis"
                        : "Request new analysis"}
                footer={null}
                width={800}
                destroyOnClose
                onCancel={() => setInputModalOpen(false)}
            >
                <Typography.Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 16 }}>
                    Add/remove documents and describe what changed.
                </Typography.Text>
                {status === ProjectAnalysisStatus.REQUIRE_USER_ACTION && (
                    <Alert
                        message="Changes requested"
                        description={analysis.requireUserActionMessage}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <InputEditor
                    state={{ project, analysis: getLatestAnalysis(project) }}
                    onSaveDraft={fetchProject}
                    onSubmit={async () => {
                        setInputModalOpen(false);
                        await fetchProject();
                        navigate(`/project/${project.id}`);
                    }}
                />
            </Modal>
        </div>
    );
};
